/**
 * Runtime accessor for the build identity inlined at Vite build time
 * (see `shared/src/build/build-info.node.ts`). Used to render the build badge
 * in the sidebar and to stamp `x-client-*` headers on every backend request.
 *
 * In dev and in non-Vite hosts (Expo, unit tests) the `VITE_BUILD_*` vars are
 * undefined — the values only carry meaning inside a produced bundle — so every
 * field falls back to `'dev'`.
 */
export interface BuildInfo {
  version: string;
  sha: string;
  shortSha: string;
  builtAt: string | null;
  /** Vite mode: 'production' | 'staging' | 'development'. */
  env: string;
}

export function getBuildInfo(): BuildInfo {
  const sha = import.meta.env.VITE_BUILD_SHA || 'dev';
  return {
    version: import.meta.env.VITE_BUILD_VERSION || 'dev',
    sha,
    shortSha: import.meta.env.VITE_BUILD_SHORT_SHA || (sha === 'dev' ? 'dev' : sha.slice(0, 7)),
    builtAt: import.meta.env.VITE_BUILD_TIME || null,
    env: import.meta.env.MODE || 'development',
  };
}

/**
 * Headers every web client attaches to backend calls so a server-side log or a
 * captured payload can be tied back to the exact frontend build that made it.
 */
export function buildInfoHeaders(): Record<string, string> {
  const info = getBuildInfo();
  return {
    'x-client-version': info.version,
    'x-client-sha': info.sha,
  };
}
